import React, { useState } from 'react'

import { CustomSelect, Dropdown } from '@fourthwall/components'

import type { SortingStrategy } from '../../types'
import * as S from './styled'

type SortingStrategyOption = {
  value: SortingStrategy
  label: string
  description: string
}

const SORTING_STRATEGY_OPTIONS: SortingStrategyOption[] = [
  {
    value: 'MANUAL',
    label: 'Manual order',
    description: 'Drag and drop products to arrange them in your preferred order.',
  },
  {
    value: 'BEST_SELLING',
    label: 'Best selling first',
    description: 'Products with the most sales will appear first in your collection.',
  },
  {
    value: 'NEWEST',
    label: 'Newest products first',
    description: 'Recently added products will appear first in your collection.',
  },
  {
    value: 'OLDEST',
    label: 'Oldest products first',
    description: 'Older products will appear first in your collection.',
  },
  {
    value: 'NAME_ASC',
    label: 'Product name (A to Z)',
    description: 'Products will be sorted alphabetically from A to Z.',
  },
  {
    value: 'NAME_DESC',
    label: 'Product name (Z to A)',
    description: 'Products will be sorted alphabetically from Z to A.',
  },
]

type SortingStrategySelectProps = {
  name?: string
  label: string
  value: SortingStrategy
  disabled?: boolean
  error?: string
  helperText?: string
  onValueChange?: (value: SortingStrategy) => void
}

export const SortingStrategySelect = ({
  name,
  label,
  value,
  disabled = false,
  error,
  helperText,
  onValueChange,
}: SortingStrategySelectProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const selectedOption = SORTING_STRATEGY_OPTIONS.find((option) => option.value === value)
  const displayValue = selectedOption?.label || ''

  const handleSelect = (selectedValue: SortingStrategy) => {
    onValueChange?.(selectedValue)
    setIsOpen(false)
  }

  const renderDropdownContent = () => {
    return SORTING_STRATEGY_OPTIONS.map((option) => (
      <Dropdown.Item
        key={option.value}
        label={
          <S.OptionContent>
            <S.OptionTitle>{option.label}</S.OptionTitle>
            <S.OptionDescription>{option.description}</S.OptionDescription>
          </S.OptionContent>
        }
        onClick={() => handleSelect(option.value)}
        active={option.value === value}
      />
    ))
  }

  return (
    <>
      <Dropdown
        isOpen={isOpen}
        content={renderDropdownContent()}
        position="bottom-start"
        maxWidth={600}
        onClose={() => setIsOpen(false)}
      >
        <CustomSelect
          name={name}
          label={label}
          value={displayValue}
          disabled={disabled}
          error={error}
          onSelect={() => !disabled && setIsOpen(!isOpen)}
        />
      </Dropdown>
      {helperText && <S.HelperText>{helperText}</S.HelperText>}
    </>
  )
}
